import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, GitBranch } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface DetailsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  task: {
    id: string;
    name: string;
    status: string;
    agent?: string | {
      name: string;
      avatar?: string;
    };
    startedAt?: string;
    duration?: string;
  } | null;
  streamName?: string;
}
export function DetailsPopover({
  isOpen,
  onClose,
  task,
  streamName
}: DetailsPopoverProps) {
  if (!task) return null;
  const agentName = typeof task.agent === 'string' ? task.agent : task.agent?.name;
  return <AnimatePresence>
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/20 z-50" />

          {/* Popover */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.15
      }} className="fixed top-24 right-[47%] w-[400px] bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-surface-600">
              <h3 className="font-semibold text-text-primary">Task Details</h3>
              <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">
                  Task Information
                </h4>
                <div className="space-y-2.5 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-text-muted">Status</span>
                    <Badge variant={task.status === 'completed' ? 'success' : task.status === 'running' ? 'primary' : task.status === 'attention' ? 'warning' : 'default'}>
                      {task.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Agent</span>
                    <span className="text-text-primary">
                      {agentName || 'Unassigned'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-muted">Started</span>
                    <span className="text-text-primary">
                      {task.startedAt || '2h ago'}
                    </span>
                  </div>
                  {task.duration && <div className="flex justify-between">
                      <span className="text-text-muted">Duration</span>
                      <span className="text-text-primary">{task.duration}</span>
                    </div>}
                  {streamName && <div className="flex justify-between">
                      <span className="text-text-muted">Stream</span>
                      <span className="text-text-primary">{streamName}</span>
                    </div>}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-2">
                  Description
                </h4>
                <p className="text-sm text-text-primary leading-relaxed">
                  This task is part of the {streamName || 'current stream'}. The
                  agent is working on completing the requirements and will
                  update progress as work continues.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-text-secondary mb-3">
                  Quick Actions
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="secondary" size="sm" className="w-full justify-center">
                    <Play className="w-3.5 h-3.5 mr-1.5" />
                    Resume
                  </Button>
                  <Button variant="secondary" size="sm" className="w-full justify-center">
                    <GitBranch className="w-3.5 h-3.5 mr-1.5" />
                    Branch
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}